/* ═══════════════════════════════════════════════════
   TrackLab v2 — Feedback Widget
   Thumbs up/down on model-generated content.
   Batched via TL.analytics.track('feedback', ...).
   ═══════════════════════════════════════════════════ */

(function () {
  'use strict';

  TL.feedback = {};

  var STORAGE_KEY = 'tl_fb_done';

  // ── SessionStorage helpers (deduplication) ──

  function _getDone() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function _markDone(entityId) {
    var done = _getDone();
    done[entityId] = true;
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(done)); } catch (e) { /* quota */ }
  }

  function _isDone(entityId) {
    return !!_getDone()[entityId];
  }

  // ── Collect data attributes from .tl-feedback container ──

  function _getData(container) {
    var d = container.dataset;
    var data = {
      entity_type: d.entityType || '',
      entity_id: d.entityId || '',
      sentiment: ''
    };
    if (d.raceId) data.race_id = d.raceId;
    if (d.eventId) data.event_id = d.eventId;
    if (d.horse) data.horse = d.horse;
    if (d.score) data.score = Math.round(Number(d.score)) || 0;
    if (d.venue) data.venue = d.venue;
    if (d.predictionOutcome) data.prediction_outcome = d.predictionOutcome;

    // Score tier from TL.scoreTier
    if (data.score && TL.scoreTier) {
      data.score_tier = TL.scoreTier(data.score).label;
    }

    // Element context — immediate parent of the feedback widget
    var parent = container.parentElement;
    if (parent) {
      data.element_tag = parent.tagName.toLowerCase();
      if (parent.id) data.element_id = parent.id;
      if (parent.className) data.element_classes = parent.className;
    }

    // Content snapshot — find nearest [data-fb-content] element.
    // Search order:
    //   1. preceding siblings (and any [data-fb-content] descendants)
    //   2. forward siblings (and any [data-fb-content] descendants)
    //   3. parent.querySelector (any descendant of the parent)
    //   4. heading-section special case: if parent is H1-H6, also walk
    //      forward from the heading itself, skipping .sec-sub
    //   5. title-embedded special case (operator rework r1 of PR #14):
    //      widgets that opt in with `data-fb-content-scope="title"`
    //      treat their parent as a title element and walk forward
    //      siblings of the title to find the body that follows. Used
    //      by .fest-summary-label, .fr-race-name, and .pick-rank
    //      placements where the widget lives inside the title and the
    //      body is the title's next sibling.
    var contentEl = null;
    var sib = container.previousElementSibling;
    while (sib) {
      if (sib.hasAttribute('data-fb-content')) { contentEl = sib; break; }
      contentEl = sib.querySelector('[data-fb-content]');
      if (contentEl) break;
      sib = sib.previousElementSibling;
    }
    if (!contentEl) {
      sib = container.nextElementSibling;
      while (sib) {
        if (sib.hasAttribute('data-fb-content')) { contentEl = sib; break; }
        contentEl = sib.querySelector('[data-fb-content]');
        if (contentEl) break;
        sib = sib.nextElementSibling;
      }
    }
    if (!contentEl && parent) {
      contentEl = parent.querySelector('[data-fb-content]');
    }
    if (!contentEl && parent && /^H[1-6]$/.test(parent.tagName)) {
      var next = parent.nextElementSibling;
      // Skip .sec-sub subtitle element if present
      if (next && next.classList.contains('sec-sub')) next = next.nextElementSibling;
      if (next) {
        if (next.hasAttribute('data-fb-content')) { contentEl = next; }
        else { contentEl = next.querySelector('[data-fb-content]'); }
      }
    }
    if (!contentEl && d.fbContentScope === 'title' && parent) {
      // Walk forward siblings of the title's ancestors. Stops at the
      // first ancestor whose forward-sibling subtree contains
      // [data-fb-content], or when an ancestor with data-fb-scope-stop
      // is hit (defensive boundary for un-related sections), or the
      // body root.
      var ancestor = parent;
      while (ancestor && ancestor !== document.body) {
        var titleSib = ancestor.nextElementSibling;
        while (titleSib) {
          if (titleSib.hasAttribute('data-fb-content')) { contentEl = titleSib; break; }
          contentEl = titleSib.querySelector('[data-fb-content]');
          if (contentEl) break;
          titleSib = titleSib.nextElementSibling;
        }
        if (contentEl) break;
        if (ancestor.hasAttribute && ancestor.hasAttribute('data-fb-scope-stop')) break;
        ancestor = ancestor.parentElement;
      }
    }
    if (contentEl) {
      data.content_snapshot = (contentEl.textContent || '').trim().slice(0, 300);
    }

    return data;
  }

  // ── Mark a container as submitted ──

  function _markSubmitted(container, sentiment) {
    container.classList.add('tl-fb-done');
    var btns = container.querySelectorAll('.tl-fb-btn');
    for (var i = 0; i < btns.length; i++) {
      btns[i].disabled = true;
      if (sentiment === 'up' && btns[i].classList.contains('tl-fb-up')) {
        btns[i].classList.add('active');
      }
      if (sentiment === 'down' && btns[i].classList.contains('tl-fb-down')) {
        btns[i].classList.add('active');
      }
    }
    // Remove any open form
    var form = container.querySelector('.tl-fb-form');
    if (form) form.remove();

    // Show confirmation, then fade out after 5s
    var msg = document.createElement('span');
    msg.className = 'tl-fb-thanks';
    msg.textContent = 'Thanks!';
    container.appendChild(msg);
    setTimeout(function () {
      msg.classList.add('tl-fb-thanks-out');
      msg.addEventListener('transitionend', function () { msg.remove(); });
    }, 5000);
  }

  // ── Send feedback event ──

  function _send(container, sentiment, comment) {
    var data = _getData(container);
    data.sentiment = sentiment;
    if (comment) data.comment = comment;
    TL.analytics.track('feedback', data);
    _markDone(data.entity_id);
    _markSubmitted(container, sentiment);
  }

  // ── Show comment form on thumbs down ──

  var MOBILE_BP = 700;

  function _isMobile() {
    return window.innerWidth <= MOBILE_BP;
  }

  var MAX_COMMENT = 500;

  function _resetWidget(container) {
    var btns = container.querySelectorAll('.tl-fb-btn');
    for (var i = 0; i < btns.length; i++) {
      btns[i].disabled = false;
      btns[i].classList.remove('active');
    }
    var form = container.querySelector('.tl-fb-form');
    if (form) form.remove();
  }

  function _buildFormContent(container, onSend, onSkip, onCancel) {
    var frag = document.createDocumentFragment();

    var textarea = document.createElement('textarea');
    textarea.className = 'tl-fb-comment';
    textarea.maxLength = MAX_COMMENT;
    textarea.placeholder = 'Tell us what was wrong (optional)';
    textarea.rows = 3;

    var counter = document.createElement('div');
    counter.className = 'tl-fb-counter';
    counter.textContent = MAX_COMMENT + '/' + MAX_COMMENT;
    textarea.addEventListener('input', function () {
      var remaining = MAX_COMMENT - textarea.value.length;
      counter.textContent = remaining + '/' + MAX_COMMENT;
      counter.classList.toggle('tl-fb-counter-low', remaining <= 50);
    });

    var actions = document.createElement('div');
    actions.className = 'tl-fb-actions';

    var sendBtn = document.createElement('button');
    sendBtn.className = 'tl-fb-submit';
    sendBtn.textContent = 'Send';
    sendBtn.type = 'button';

    var skipBtn = document.createElement('button');
    skipBtn.className = 'tl-fb-skip';
    skipBtn.textContent = 'Skip';
    skipBtn.type = 'button';

    var cancelBtn = document.createElement('button');
    cancelBtn.className = 'tl-fb-cancel';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.type = 'button';

    actions.appendChild(sendBtn);
    actions.appendChild(skipBtn);
    actions.appendChild(cancelBtn);
    frag.appendChild(textarea);
    frag.appendChild(counter);
    frag.appendChild(actions);

    sendBtn.addEventListener('click', function () {
      onSend(textarea.value.trim());
    });
    skipBtn.addEventListener('click', function () {
      onSkip();
    });
    cancelBtn.addEventListener('click', function () {
      onCancel();
    });

    return { frag: frag, textarea: textarea };
  }

  function _closeSheet() {
    var sheet = document.querySelector('.mob-fb-sheet');
    var scrim = document.querySelector('.mob-fb-scrim');
    if (sheet) sheet.remove();
    if (scrim) scrim.remove();
  }

  function _showSheetForm(container) {
    _closeSheet();

    var scrim = document.createElement('div');
    scrim.className = 'mob-tip-scrim mob-fb-scrim';
    document.body.appendChild(scrim);

    var sheet = document.createElement('div');
    sheet.className = 'mob-tip-sheet mob-fb-sheet m-sheet';

    var header = document.createElement('div');
    header.className = 'mob-tip-sheet-header';
    header.innerHTML =
      '<span class="mob-tip-sheet-title">What went wrong?</span>' +
      '<button class="mob-tip-sheet-close" aria-label="Close">&times;</button>';

    var body = document.createElement('div');
    body.className = 'mob-tip-sheet-body';

    function cancelSheet() { _closeSheet(); _resetWidget(container); }

    var form = _buildFormContent(container,
      function (comment) { _closeSheet(); _send(container, 'down', comment); },
      function () { _closeSheet(); _send(container, 'down', ''); },
      cancelSheet
    );
    body.appendChild(form.frag);
    sheet.appendChild(header);
    sheet.appendChild(body);
    document.body.appendChild(sheet);

    header.querySelector('.mob-tip-sheet-close').addEventListener('click', cancelSheet);
    scrim.addEventListener('click', cancelSheet);

    form.textarea.focus();
  }

  function _showInlineForm(container) {
    if (container.querySelector('.tl-fb-form')) return;

    var form = document.createElement('div');
    form.className = 'tl-fb-form';

    var content = _buildFormContent(container,
      function (comment) { _send(container, 'down', comment); },
      function () { _send(container, 'down', ''); },
      function () { _resetWidget(container); }
    );
    form.appendChild(content.frag);
    container.appendChild(form);

    content.textarea.focus();
  }

  function _showForm(container) {
    if (_isMobile()) {
      _showSheetForm(container);
    } else {
      _showInlineForm(container);
    }
  }

  // ── Event delegation — handles all .tl-feedback buttons ──

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.tl-fb-btn');
    if (!btn) return;

    var container = btn.closest('.tl-feedback');
    if (!container) return;

    var entityId = container.dataset.entityId || '';
    if (!entityId || _isDone(entityId)) return;

    if (btn.classList.contains('tl-fb-up')) {
      _send(container, 'up', '');
    } else if (btn.classList.contains('tl-fb-down')) {
      // Highlight the down button, show comment form
      btn.classList.add('active');
      container.querySelector('.tl-fb-up').disabled = true;
      _showForm(container);
    }
  });

  // ── Restore submitted state for already-voted items ──

  TL.feedback.restore = function (root) {
    var widgets = (root || document).querySelectorAll('.tl-feedback');
    for (var i = 0; i < widgets.length; i++) {
      var id = widgets[i].dataset.entityId || '';
      if (id && _isDone(id)) {
        _markSubmitted(widgets[i], '');
      }
    }
  };

  // Restore on initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { TL.feedback.restore(); });
  } else {
    TL.feedback.restore();
  }
})();
